/**
 * AchievementBoard - Achievement display with category progress
 * Shows category achievements, star progress, and Aitsa badge
 *
 * Stars are dynamically colored based on category border_color.
 * Badges are grayscale until all 3 stars are earned.
 */

import { t, getCurrentLanguage } from '../i18n/config.js';
import { api } from '../services/api.js';

// Character illustration mapping for category cards
const CATEGORY_ILLUSTRATIONS = {
  kuns: '/svg/branding/KassiekunsTema.svg',
  plaas: '/svg/branding/KassieplaasTema.svg',
  vervoer: '/svg/branding/KassievervoerTema.svg',
  wildtuin: '/svg/branding/KassiewildtuinTema.svg',
  sport: '/svg/branding/KassiesportTema.svg',
};

// Helper function to get illustration URL with flexible matching
function getIllustrationUrl(category) {
  const categoryId = (category.id || '').toLowerCase();
  const categoryNameAf = (category.name_af || '').toLowerCase();
  const categoryNameEn = (category.name_en || '').toLowerCase();

  // Try direct ID match first
  if (CATEGORY_ILLUSTRATIONS[categoryId]) {
    return CATEGORY_ILLUSTRATIONS[categoryId];
  }

  // Try matching by Afrikaans name
  if (CATEGORY_ILLUSTRATIONS[categoryNameAf]) {
    return CATEGORY_ILLUSTRATIONS[categoryNameAf];
  }

  // Try matching by English name
  const englishToAfrikaans = {
    arts: 'kuns',
    farm: 'plaas',
    transport: 'vervoer',
    wildlife: 'wildtuin',
    sport: 'sport',
  };

  if (englishToAfrikaans[categoryNameEn]) {
    return CATEGORY_ILLUSTRATIONS[englishToAfrikaans[categoryNameEn]];
  }

  // Fallback: return empty string
  console.warn('No illustration found for category:', category);
  return '';
}

export class AchievementBoard {
  constructor(container, router, profileData) {
    this.container = container;
    this.router = router;
    this.profileData = profileData;
    this.achievementData = null;
    this.unreadMessages = 0;
  }

  /**
   * Render achievement board
   */
  async render() {
    // Clear container
    this.container.innerHTML = '';

    // Adult profiles: show the Kassie artist illustration alongside heading, message and switch button
    if (this.profileData?.type === 'adult') {
      this.container.innerHTML = `
        <div class="achievement-board">
          <div class="achievement-board__folder">
            <div class="achievement-board__folder-tab">
              <h2 class="achievement-board__folder-title">${t('child.achievements.title') || 'Spogbord'}</h2>
            </div>
            <div class="achievement-board__folder-body">
              <div class="achievement-board__adult-card">
                <div class="achievement-board__adult-illustration">
                  <img src="/svg/branding/KassiekunsTema.svg" alt="" aria-hidden="true" class="achievement-board__adult-image" />
                </div>
                <div class="achievement-board__adult-content">
                  <h3 class="achievement-board__adult-heading">${t('child.achievements.adultTitle') || 'Grootmens Profiel'}</h3>
                  <p class="achievement-board__adult-text">${t('child.achievements.adultMessage') || 'Die spogbord hou nie boek van wat die grootmense op die Kassie toep doen nie'}</p>
                  <button class="achievement-board__switch-btn" id="switchToChildBtn">
                    ${t('child.achievements.switchToChild') || "Meld aan as 'n kind"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;

      // Wire switch button to navigate to profile screen
      document.getElementById('switchToChildBtn')?.addEventListener('click', () => {
        this.router.navigate('profile', this.profileData);
      });

      // Show the sub-message as a wiggling toast underneath the button.
      // Mounted inside the card so it is destroyed when the view changes —
      // otherwise navigating away via a nav icon before the 5s timeout would
      // leave it hanging over the next page.
      const switchBtn = document.getElementById('switchToChildBtn');
      const toastHost = this.container.querySelector('.achievement-board__adult-card');
      import('../utils/toast.js').then(({ showToast }) => {
        showToast(
          t('child.achievements.adultMessageSub') || 'Loer by jou kinders se spogbord vir hulle vordering',
          'info',
          5000,
          { anchorEl: switchBtn, wiggle: true, parent: toastHost }
        );
      });

      return;
    }

    // Show loading state
    this.container.innerHTML = `
      <div class="achievement-board loading"></div>
    `;

    // Show Kassie loader
    window.showLoader();

    try {
      // Load achievement data
      await this.loadAchievementData();

      // Render achievement interface
      this.renderAchievementInterface();
    } catch (error) {
      console.error('Error loading achievement board:', error);
      this.renderError();
    }
  }

  /**
   * Load achievement data from API
   */
  async loadAchievementData() {
    console.log('🏆 Loading achievements for profile:', this.profileData.id);

    try {
      const { getDeviceId } = await import('../services/deviceAuth.js');
      const deviceId = getDeviceId();

      const achievements = await api.get(
        `/api/device/profiles/${this.profileData.id}/achievements`,
        {
          deviceId: deviceId,
        }
      );
      this.achievementData = achievements;
      console.log('✅ Loaded achievements from API:', this.achievementData);
    } catch (error) {
      console.error('❌ Failed to load achievements:', error);
      // Set empty data instead of placeholder - no fallback to dummy data
      this.achievementData = {
        categories: [],
        overallCompleted: 0,
        aitsaEarned: false,
      };
    }

    // Load unread message count
    try {
      const { getDeviceId } = await import('../services/deviceAuth.js');
      const deviceId = getDeviceId();
      const response = await api.get(`/api/device/profiles/${this.profileData.id}/inbox`, {
        deviceId: deviceId,
      });
      const messages = response.messages || [];
      this.unreadMessages = messages.filter((m) => !m.opened).length;
      console.log('✅ Loaded message count:', this.unreadMessages);
    } catch (error) {
      console.warn('Could not load messages:', error);
      this.unreadMessages = 0;
    }
  }

  /**
   * Convert hex color to CSS filter for coloring white SVG
   * @param {string} hex - Hex color code (e.g., '#4CAF50')
   * @returns {string} CSS filter string
   */
  hexToFilter(hex) {
    // Handle missing or invalid hex
    if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
      hex = '#00bcd4'; // Default cyan
    }

    // Parse hex to RGB
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    // Convert RGB to HSL
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;

    let h = 0;
    let s = 0;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }

    const hueRotate = Math.round(h * 360);
    const saturate = Math.round(s * 100);
    const brightness = Math.round(l * 100);

    // Build filter to transform white to target color
    return `brightness(0) saturate(100%) invert(${brightness}%) sepia(100%) saturate(${saturate * 10}%) hue-rotate(${hueRotate}deg)`;
  }

  /**
   * Render achievement interface
   */
  renderAchievementInterface() {
    const categories = this.achievementData.categories || [];

    // Hide Kassie loader
    window.hideLoader();

    // Check if we have any categories to display
    if (categories.length === 0) {
      this.renderEmptyState();
      return;
    }

    this.container.innerHTML = `
      <div class="achievement-board">
        <div class="achievement-cards-container">
          ${categories.map((category, index) => this.renderCategoryAchievement(category, index)).join('')}
        </div>
      </div>
    `;

    // Bind events
    this.bindEvents();
  }

  /**
   * Render empty state when no categories exist
   */
  renderEmptyState() {
    this.container.innerHTML = `
      <div class="achievement-board">
        <div class="achievement-cards-container">
          <div class="achievement-empty">
            <div class="achievement-empty-icon"><img src="./svg/icons/ModalFramesSpogbordIcon-1.svg" alt="Achievement trophy" width="128" height="128"></div>
            <p class="achievement-empty-message">
              ${t('child.achievements.noCategories') || 'No achievements yet. Start exploring content to earn stars!'}
            </p>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render category achievement with star progress
   * @param {Object} category - Category data from API
   * @param {number} index - Category index for positioning
   */
  renderCategoryAchievement(category, index) {
    const stars = category.stars || 0;
    const totalStars = category.totalStars || 3;
    const completed = stars >= totalStars;
    const badgeUrl = category.badge_url || '';

    // Star images - yellow for earned, white for unearned
    const yellowStarUrl = '/svg/components/YellowStar.svg';
    const whiteStarUrl = '/svg/components/WhiteStar.svg';

    // Get category name in current language
    const currentLang = getCurrentLanguage();
    const categoryName = currentLang === 'af' ? category.name_af : category.name_en;

    // Get character illustration URL from mapping
    const illustrationUrl = getIllustrationUrl(category);

    // Border color matches the module card category border
    const borderColor = category.border_color || '#44c1c5';

    return `
      <div class="category-card-wrapper">
        <h2 class="category-label" style="border-color: ${borderColor}; border-bottom-color: #ffffff;">${categoryName}</h2>
        <div class="category-card" data-category="${category.id}" data-completed="${completed}" style="border: 18px solid ${borderColor};">
          <div class="category-card-content">
            <div class="category-card-left">
              <img src="${illustrationUrl}" alt="${categoryName}" class="category-illustration">
            </div>
            <div class="category-card-right">
              <div class="achievement-badge"
                   style="background-image: url('${badgeUrl}')">
              </div>
              <div class="achievement-stars">
                ${Array.from(
                  { length: totalStars },
                  (_, i) => `
                  <span class="achievement-star achievement-star--position-${i + 1} ${i < stars ? 'achievement-star--filled' : 'achievement-star--empty'}">
                    <img src="${i < stars ? yellowStarUrl : whiteStarUrl}"
                         alt="${i < stars ? 'Earned star' : 'Locked star'}"
                         class="achievement-star-img">
                  </span>
                `
                ).join('')}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Bind events
   */
  bindEvents() {
    // Category card click handlers
    const categoryCards = this.container.querySelectorAll('.category-card-wrapper .category-card');
    categoryCards.forEach((card) => {
      card.addEventListener('click', () => {
        const categoryId = card.dataset.category;
        this.showAchievementDetail(categoryId);
      });
    });
  }

  /**
   * Show achievement detail modal with completed modules
   */
  showAchievementDetail(categoryId) {
    const category = this.achievementData.categories.find((c) => c.id === categoryId);
    if (!category) return;

    console.log('📊 Showing achievement detail for:', category.name, category);

    // Get current language and use appropriate category name
    const currentLang = getCurrentLanguage();
    const categoryName = currentLang === 'af' ? category.name_af : category.name_en;

    // Create modal
    const modal = document.createElement('div');
    modal.className = 'achievement-detail-modal open';

    const completedModules = category.completed_modules || [];

    // Get module title based on current language
    const getModuleTitle = (module) => {
      if (currentLang === 'af') {
        return module.title_af || module.title || module.name || 'Module';
      } else {
        return module.title_en || module.title || module.name || 'Module';
      }
    };

    // Border color matches the category card
    const borderColor = category.border_color || '#44c1c5';

    modal.innerHTML = `
      <div class="achievement-detail-content" style="border: 18px solid ${borderColor};">
        <div class="achievement-detail-header">
          <div class="achievement-detail-badge"
               style="background-image: url('${category.badge_url}')"></div>
          <div class="achievement-detail-header-text">
            <h2 class="achievement-detail-title">${categoryName}</h2>
            <div class="achievement-detail-stars">
              ${Array.from({ length: category.stars || 0 }, () => '<img src="/svg/components/YellowStar.svg" alt="⭐" class="achievement-star-icon" />').join('')}
            </div>
          </div>
        </div>
        <div class="achievement-detail-progress">
          <p class="achievement-detail-progress-label">
            ${t('child.achievements.completedModules')}: ${completedModules.length}
          </p>
          ${
            completedModules.length > 0
              ? `
            <ul class="achievement-module-list">
              ${completedModules
                .map(
                  (module) => `
                <li class="achievement-module-item" style="background: color-mix(in srgb, ${borderColor} 50%, transparent);">${getModuleTitle(module)}</li>
              `
                )
                .join('')}
            </ul>
          `
              : `
            <p class="achievement-empty-modules">${t('child.achievements.noModulesYet')}</p>
          `
          }
        </div>
        <button class="achievement-detail-close" style="background: ${borderColor};">${t('child.actions.close')}</button>
      </div>
    `;

    document.body.appendChild(modal);

    // Close button
    const closeBtn = modal.querySelector('.achievement-detail-close');
    closeBtn.addEventListener('click', () => {
      modal.classList.remove('open');
      setTimeout(() => modal.remove(), 300);
    });

    // Backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.classList.remove('open');
        setTimeout(() => modal.remove(), 300);
      }
    });
  }

  /**
   * Show overall achievement detail
   */
  showOverallAchievementDetail() {
    const completedCount = this.achievementData.categories.filter((c) => c.completed).length;
    const totalCategories = this.achievementData.categories.length;
    const aitsaEarned = completedCount > 3;

    console.log(
      '🏆 Showing Aitsa badge detail. Completed:',
      completedCount,
      'Total:',
      totalCategories
    );

    // Create modal
    const modal = document.createElement('div');
    modal.className = 'achievement-detail-modal open';

    const progress = Math.min(100, (completedCount / 4) * 100); // Progress toward 4 categories (>3)

    modal.innerHTML = `
      <div class="achievement-detail-content">
        <div class="achievement-detail-header">
          <div class="achievement-detail-badge"
               style="background-image: var(--kassie-svg-components-aitsaaward)"></div>
          <h2 class="achievement-detail-title">${t('child.achievements.aitsaBadge')}</h2>
        </div>
        <div class="achievement-detail-progress">
          <p class="achievement-detail-progress-label">
            ${t('child.achievements.categoriesCompleted')}: ${completedCount} / 4
          </p>
          <div class="achievement-detail-progress-bar">
            <div class="achievement-detail-progress-fill" style="width: ${progress}%; background: var(--kassie-color-primary-cyan, #00bcd4)"></div>
          </div>
          ${
            aitsaEarned
              ? `
            <p class="achievement-aitsa-message achievement-aitsa-earned">
              ${t('child.achievements.congratulations')}
            </p>
          `
              : `
            <p class="achievement-aitsa-message achievement-aitsa-locked">
              ${t('child.achievements.needMoreCategories')}
            </p>
          `
          }
        </div>
        <button class="achievement-detail-close">${t('child.actions.close')}</button>
      </div>
    `;

    document.body.appendChild(modal);

    // Close button
    const closeBtn = modal.querySelector('.achievement-detail-close');
    closeBtn.addEventListener('click', () => {
      modal.classList.remove('open');
      setTimeout(() => modal.remove(), 300);
    });

    // Backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.classList.remove('open');
        setTimeout(() => modal.remove(), 300);
      }
    });
  }

  /**
   * Render error state
   */
  renderError() {
    this.container.innerHTML = `
      <div class="achievement-board">
        <div class="achievement-empty">
          <div class="achievement-empty-icon"><img src="./svg/icons/ModalFramesSpogbordIcon-1.svg" alt="Achievement trophy" width="128" height="128"></div>
          <p class="achievement-empty-message">
            ${t('child.errors.couldNotLoadAchievements') || 'Could not load achievements.'}
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Destroy view
   */
  destroy() {
    this.container.innerHTML = '';
  }
}
